import { useEffect, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFrame, between } from "./explainer/useFrame";
import { SceneProblem } from "./explainer/SceneProblem";
import { SceneMagic } from "./explainer/SceneMagic";
import { ScenePremium } from "./explainer/ScenePremium";
import { SceneCta } from "./explainer/SceneCta";

type Props = {
  /** Called when the user taps the final CTA at the end of the video. */
  onCta: () => void;
  /** Called when the user taps "Skip to steps". */
  onSkip: () => void;
  /** Whether to actually run the timer (paused while modal is closed). */
  playing: boolean;
};

const DURATION_MS = 20_000;

/**
 * 20-second in-app explainer. Pure React + Tailwind (no MP4, no deps).
 *
 * Frame budget at 60fps: 0..1200
 *   0–240    Problem  (chaotic sources fly in)
 *   240–660  Magic    (clean recipe materialises in a phone frame)
 *   660–1020 Premium  (5 feature cards montage)
 *   1020–1200 CTA     (price badge + button)
 *
 * The parent passes `playing=false` while the dialog is closed so the rAF
 * loop stays parked. Respects prefers-reduced-motion (the dialog falls back
 * to the static panel view in that case).
 */
export const ExplainerVideo = ({ onCta, onSkip, playing }: Props) => {
  const { frame, t, paused, toggle, replay, done } = useFrame(DURATION_MS, playing);
  const [hasFinished, setHasFinished] = useState(false);

  useEffect(() => {
    if (done && !hasFinished) setHasFinished(true);
  }, [done, hasFinished]);

  return (
    <div className="flex flex-col">
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-background to-muted aspect-[4/5] sm:aspect-video"
        aria-label="CleanPlate explainer animation"
      >
        {/* Subtle moving background dots for depth */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(hsl(var(--foreground) / 0.06) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
            transform: `translate(${(frame % 32) - 16}px, 0)`,
          }}
        />

        {between(frame, 0, 360) && <SceneProblem frame={frame} />}
        {between(frame, 240, 720) && <SceneMagic frame={frame} />}
        {between(frame, 660, 1080) && <ScenePremium frame={frame} />}
        {frame >= 1020 && <SceneCta frame={frame} />}

        {/* Progress + controls overlay */}
        <div className="absolute inset-x-3 bottom-3 flex items-center gap-2">
          <button
            type="button"
            onClick={hasFinished ? replay : toggle}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-background/80 text-foreground ring-1 ring-border backdrop-blur transition hover:bg-background"
            aria-label={hasFinished ? "Replay" : paused ? "Play" : "Pause"}
          >
            {hasFinished ? (
              <RotateCcw className="h-3.5 w-3.5" />
            ) : paused ? (
              <Play className="h-3.5 w-3.5" />
            ) : (
              <Pause className="h-3.5 w-3.5" />
            )}
          </button>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-foreground/10">
            <div
              className="h-full bg-primary transition-[width] duration-100"
              style={{ width: `${Math.min(100, t * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* CTA row */}
      <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          Skip to steps →
        </button>
        <Button
          className="h-11 w-full sm:w-auto sm:min-w-[180px]"
          onClick={onCta}
        >
          {hasFinished ? "Get started" : "Try it now"}
        </Button>
      </div>
    </div>
  );
};
